import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { isPresidentRole } from "./RoleScopeBanner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Loader2, Package } from "lucide-react";
import { downloadSynthesisActs, suggestNextActNumber } from "./actDownloads";
import { DateInput } from "./ui/date-input";

const fmtDmy = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || ""); return m ? `${m[3]}/${m[2]}/${m[1]}` : (d || "—"); };

/** «Πράξη Σύνθεσης (ZIP)» για πινάκιο × δικάσιμο Πολυμελούς — Αρ. 1ης Πράξης + Ημερομηνία Έκδοσης ανά υπόθεση. */
export const SynthesisActsButton = ({ pinakioLabel, hearingDate, compositionLabel, onDone, testId, className = "" }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [startNum, setStartNum] = useState("");
  const [dates, setDates] = useState({});
  const year = String(hearingDate || "").slice(0, 4) || String(new Date().getFullYear());

  useEffect(() => {
    if (!open) return;
    setLoading(true); setPreview(null); setDates({});
    Promise.all([
      axios.get(`${API}/pretrial-tools/bulk-assignment-preview`, { params: { court_type: "Πολυμελές", pinakio_label: pinakioLabel, hearing_date: hearingDate } })
        .then((r) => r.data).catch((e) => { toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης υποθέσεων"); return null; }),
      suggestNextActNumber({ courtType: "Πολυμελές", year, pinakioLabel }),
    ]).then(([p, n]) => {
      if (p) {
        setPreview(p);
        setDates(Object.fromEntries((p.cases || []).map((c) => [c.case_id, c.issue_date || ""])));
      }
      setStartNum(n || "");
    }).finally(() => setLoading(false));
  }, [open, pinakioLabel, hearingDate, year]);

  const rows = preview?.cases || [];
  const missing = rows.filter((c) => c.eligible !== false && !dates[c.case_id]).length;

  const run = async () => {
    const n = parseInt(String(startNum).trim(), 10);
    if (!(n >= 1)) return toast.error("Δώστε έγκυρο Αρ. 1ης Πράξης");
    if (missing > 0) return toast.error(`Συμπληρώστε Ημερομηνία Έκδοσης σε ${missing} υπόθεση/εις`);
    setBusy(true);
    const ok = await downloadSynthesisActs({ pinakioLabel, hearingDate, startingActNumber: n, format: "docx", issueDates: dates });
    setBusy(false);
    if (ok) { setOpen(false); onDone?.(); }
  };

  if (isPresidentRole(user)) return null;
  return (
    <>
      <Button variant="ghost" size="sm" className={`h-7 text-xs gap-1 text-violet-800 hover:text-violet-950 ${className}`} onClick={() => setOpen(true)}
              title="Πράξεις Ορισμού Σύνθεσης για τις υποθέσεις της δικασίμου με την ανατεθειμένη σύνθεση (ZIP, χωρίς email)" data-testid={testId}>
        <Package className="w-3.5 h-3.5" />Πράξη Σύνθεσης (ZIP)
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-w-3xl" data-testid="synthesis-acts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-violet-700" />Πράξεις Ορισμού Σύνθεσης (ZIP)</DialogTitle>
            <DialogDescription>
              Πινάκιο <b>{pinakioLabel}</b> · Δικάσιμος <b>{fmtDmy(hearingDate)}</b>{compositionLabel ? <> · Σύνθεση «<b>{compositionLabel}</b>»</> : null}.
              Η Ημερομηνία Έκδοσης δηλώνεται ανά υπόθεση (προεπιλογή: 1η ημέρα μετά το κλείσιμο της Προσθήκης) και αποθηκεύεται μαζί με την Πράξη.
            </DialogDescription>
          </DialogHeader>
          {loading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div> : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Αρ. 1ης Πράξης <span className="text-red-600">*</span></Label>
                  <Input type="number" min="1" value={startNum} onChange={(e) => setStartNum(e.target.value)} className="h-9" data-testid="synthesis-acts-start" />
                  <p className="text-[10px] text-slate-400 mt-1">+1 ανά υπόθεση · έτος {year}. Υπάρχοντες αριθμοί διατηρούνται.</p>
                </div>
                <div className="col-span-2 flex items-end gap-2">
                  <Label className="text-xs">Κοινή ημερομηνία σε όλες:</Label>
                  <DateInput className="h-9 w-44" onChange={(e) => { const v = e.target.value; if (v) setDates(Object.fromEntries(rows.map((c) => [c.case_id, v]))); }} data-testid="synthesis-acts-date-all" />
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 sticky top-0"><th className="p-2 text-left">#</th><th className="p-2 text-left">ΓΑΚ</th><th className="p-2 text-left">Αντικείμενο</th><th className="p-2 text-center">Αρ. Πράξης</th><th className="p-2 text-center">Ημερομηνία Έκδοσης *</th></tr></thead>
                  <tbody>
                    {rows.map((c, i) => (
                      <tr key={c.case_id} className={`border-t ${c.eligible === false && !c.has_act ? "opacity-50" : ""}`} data-testid={`synthesis-acts-row-${c.case_id}`}>
                        <td className="p-2 text-slate-400">{i + 1}</td>
                        <td className="p-2 font-mono">{c.gak}</td>
                        <td className="p-2 truncate max-w-[220px]" title={c.subject}>{c.subject}</td>
                        <td className="p-2 text-center">{c.has_act ? <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700">{c.existing_act}</Badge> : <span className="text-slate-400">νέος</span>}</td>
                        <td className="p-2 text-center">
                          <DateInput value={dates[c.case_id] || ""} onChange={(e) => setDates((d) => ({ ...d, [c.case_id]: e.target.value }))} className="h-8 w-40 mx-auto" data-testid={`synthesis-acts-date-${c.case_id}`} />
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-slate-400">Δεν βρέθηκαν υποθέσεις</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="synthesis-acts-cancel">Άκυρο</Button>
            <Button onClick={run} disabled={busy || loading || rows.length === 0} className="bg-violet-700 hover:bg-violet-800" data-testid="synthesis-acts-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />} Έκδοση ZIP ({rows.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
